/**
 * Selections context — shared state for the discovery surfaces.
 *
 * Cards, modals, the right rail, and the comparison page all read and mutate
 * the same state. This provider owns the document, exposes selectors per
 * entity type, and persists changes to /api/selections optimistically.
 *
 * The cycle order matches the brief:
 *   undefined → considering → saved → undefined
 *   disqualified is a separate negative action, set/cleared via setState/undo.
 *
 * Usage:
 *   <SelectionsProvider>
 *     <DiscoveryPage />
 *   </SelectionsProvider>
 *
 *   const { getState, cycle, setState, undisqualify } = useSelections('companies');
 */
import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import { getSelections, patchSelection } from '../services/selectionService';

const SelectionsContext = createContext(null);

/** Cycle order for the primary toggle. Disqualified is OUT of this cycle. */
const CYCLE_NEXT = {
  undefined: 'considering',
  considering: 'saved',
  saved: undefined,
};

export function SelectionsProvider({ children }) {
  const [doc, setDoc] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getSelections()
      .then((d) => { if (!cancelled) setDoc(d || {}); })
      .catch((e) => { if (!cancelled) setError(e); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  /**
   * Optimistic write. We update local state immediately, then PATCH; on
   * failure, we roll back and surface the error. The brief's UX assumes
   * toggle clicks feel instant — server round-trips for every cycle would
   * miss that bar.
   */
  const writeOne = useCallback(async (entityType, entityId, nextState) => {
    setDoc((prev) => {
      const bucket = { ...(prev[entityType] || {}) };
      if (nextState == null) {
        delete bucket[entityId];
      } else {
        bucket[entityId] = nextState;
      }
      const next = { ...prev };
      if (Object.keys(bucket).length) next[entityType] = bucket;
      else delete next[entityType];
      return next;
    });

    try {
      const server = await patchSelection(entityType, entityId, nextState);
      // Reconcile with server (in case other tabs / sessions diverged).
      setDoc(server || {});
    } catch (e) {
      // Roll back by re-fetching truth.
      try { setDoc(await getSelections()); } catch { /* swallow */ }
      setError(e);
    }
  }, []);

  const value = useMemo(() => ({
    doc,
    loading,
    error,
    /** All entries for one type as a flat array of [id, state] pairs. */
    listType: (entityType) => Object.entries(doc[entityType] || {}),
    /** Get one entity's state (or undefined). */
    getState: (entityType, entityId) => doc[entityType]?.[entityId],
    /** Cycle the primary toggle: undefined → considering → saved → undefined. */
    cycle: (entityType, entityId) => {
      const current = doc[entityType]?.[entityId];
      const next = CYCLE_NEXT[current ?? 'undefined'];
      return writeOne(entityType, entityId, next);
    },
    /** Hard-set a state (used for "Disqualify" and "Save all" bulk actions). */
    setState: (entityType, entityId, state) => writeOne(entityType, entityId, state),
    /** Clear (undo) — sets state to undefined. */
    undo: (entityType, entityId) => writeOne(entityType, entityId, null),
  }), [doc, loading, error, writeOne]);

  return (
    <SelectionsContext.Provider value={value}>
      {children}
    </SelectionsContext.Provider>
  );
}

/**
 * Hook to access the selection store. Pass an entityType to get partially
 * applied helpers; omit it to get the raw store (rare).
 *
 *   const { getState, cycle, setState, undo, listType } = useSelections('companies');
 *   const state = getState('stripe.com');
 *   <SelectionToggle state={state} onCycle={() => cycle('stripe.com')} />
 */
export function useSelections(entityType) {
  const ctx = useContext(SelectionsContext);
  if (!ctx) {
    throw new Error('useSelections must be used inside <SelectionsProvider>');
  }
  if (!entityType) return ctx;
  return {
    doc: ctx.doc,
    loading: ctx.loading,
    error: ctx.error,
    listType: () => ctx.listType(entityType),
    getState: (id) => ctx.getState(entityType, id),
    cycle: (id) => ctx.cycle(entityType, id),
    setState: (id, state) => ctx.setState(entityType, id, state),
    undo: (id) => ctx.undo(entityType, id),
  };
}
